<!--
  - Copyright (c) 2024-2025. Haodong JU
  -->

<template>
  <div>
    <page-header>
      套餐详情
      <template #action>
        <primary-button
          icon="mdi-plus"
          text="新建套餐"
          @click="editingObject=null;showDialog=true"
        />
      </template>
    </page-header>
    <div
      v-if="supportsEffectiveDate && list.length > 0"
      class="d-flex flex-wrap mt-4"
      style="gap: 8px"
    >
      <v-chip
        v-for="f in periodFilters"
        :key="f.value"
        :color="periodFilter === f.value ? 'primary' : 'grey lighten-3'"
        :text-color="periodFilter === f.value ? 'white' : 'black'"
        small
        @click="periodFilter = f.value"
      >
        {{ f.text }} · {{ f.count }}
      </v-chip>
    </div>
    <loading-provider
      :loading="loading"
      class="mt-4"
    >
      <div style="display: grid;grid-template-columns: 1fr;grid-gap: 4px">
        <v-card
          v-for="r in filteredList"
          :key="r.id"
          class="px-4 py-3 pt-4
"
          elevation="0"
          @click="edit(r)"
        >
          <div class="d-flex">
            <div class="text-body-1 mr-2 font-weight-black">
              # {{ r.price }}
            </div>
            <v-chip
              :color="r.active ? 'success' : 'grey'"
              class="mr-2"
              small
              text-color="white"
            >
              {{ r.active ? '已激活' : '未激活' }}
            </v-chip>
            <v-spacer />
            <v-btn
              class="ml-2"
              color="grey lighten-4"
              elevation="0"
              small
              @click.stop="remove(r)"
            >
              {{ $t('delete') }}
            </v-btn>
          </div>
          <div
            v-if="supportsEffectiveDate"
            class="my-1"
          >
            <v-chip
              :color="periodInfo(r).color"
              outlined
              small
            >
              <v-icon
                left
                small
              >
                mdi-calendar-range
              </v-icon>
              {{ periodLabel(r) }}<template v-if="periodInfo(r).status !== 'always'">
                · {{ periodInfo(r).text }}
              </template>
            </v-chip>
          </div>
          <div
            class="my-1 d-flex flex-wrap"
            style="gap: 4px"
          >
            <v-chip
              outlined
              small
            >
              <v-icon
                left
                small
              >
                mdi-door
              </v-icon>
              适用包厢：{{ roomLabel(r) }}
            </v-chip>
            <v-chip
              outlined
              small
            >
              <v-icon
                left
                small
              >
                mdi-clock-outline
              </v-icon>
              开始时段：{{ startRangeLabel(r) }}
            </v-chip>
          </div>
          <div>
            {{ r.comboBody }}
          </div>
        </v-card>
        <div
          v-if="!loading && list.length > 0 && filteredList.length === 0"
          class="text-body-2 grey--text pa-4"
        >
          没有{{ currentFilterText }}的套餐
        </div>
      </div>
    </loading-provider>
    <v-dialog
      v-model="showDialog"
      max-width="500"
    >
      <base-form
        :editing-object="editingObject"
        :loading="dialogLoading"
        ref="comboForm"
        :schema="schemas"
        @close="showDialog=false"
        @holder-change="formHolder = $event"
        @submit="save"
      >
        新建/编辑套餐
      </base-form>
    </v-dialog>
  </div>
</template>

<script>
import PageHeader from '@/views/widget/typography/PageHeader.vue'
import PrimaryButton from '@/views/AadenPages/components/PrimaryButton.vue'
import LoadingProvider from '@/views/widget/LoadingProvider.vue'
import BaseForm from '@/model/baseForm/BaseForm/BaseForm.vue'
import { sureToDelete } from '@/model/utils/utils'
import { VSelect, VTextarea } from 'vuetify/lib/components'
import { deletePriceCombo, getAllPriceCombo, savePriceCombo } from '@/model/dataLayer/service/reservation/priceCombo'
import dayjs from 'dayjs'
import Table from '@/model/dataLayer/api/tableAndPerson/Table'
import { checkReservationStatus } from '@/model/dataLayer/api/newReservation/reservation'

// 生效期状态：一直有效 / 现在生效 / 以后生效 / 已过期（按今天判断，只用于后台展示；后端按预订日期判断）
const PERIOD_STATUS = {
  always: { text: '一直有效', color: 'grey darken-1' },
  current: { text: '现在生效', color: 'success' },
  upcoming: { text: '以后生效', color: 'blue' },
  expired: { text: '已过期', color: 'grey' }
}

function emptyToNull (v) {
  return v || null
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/

// "HH:mm" 加减整点小时，按 24 小时取模（实际时间 ↔ 后台营业时间 = 实际 − businessHourOffset）
function shiftHHmm (hhmm, hours) {
  if (!hhmm || !HHMM.test(hhmm)) return null
  const [h, m] = hhmm.split(':').map(Number)
  const nh = (((h + hours) % 24) + 24) % 24
  return String(nh).padStart(2, '0') + ':' + String(m).padStart(2, '0')
}

export default {
  name: 'PriceCombo',
  components: {
    BaseForm,
    LoadingProvider,
    PrimaryButton,
    PageHeader
  },
  data: function () {
    return {
      dialogLoading: false,
      baseFromInfo: {},
      showDialog: null,
      editingObject: null,
      list: [],
      loading: false,
      // 后端有 /all 接口（cloud #461）才支持生效日期；没有时不显示日期相关的东西
      supportsEffectiveDate: false,
      periodFilter: 'all',
      // 表单里正在编辑的对象（BaseForm 的 holder-change 回传），给日期校验用
      formHolder: {},
      // 门店包厢（本机桌台里 tableType = Room 的，id 就是云端的 tableId，和「特殊价格」页同一来源）
      roomList: [],
      // 营业时间偏移（小时）；取不到时为 null，这时不显示也不让填开始时段，免得换算错
      businessHourOffset: null
    }
  },
  computed: {
    today () {
      return dayjs().format('YYYY-MM-DD')
    },
    periodFilters () {
      const count = (pred) => this.list.filter(pred).length
      const status = (r) => this.periodInfo(r).status
      return [
        { value: 'all', text: '全部', count: this.list.length },
        { value: 'current', text: '现在生效', count: count(r => ['always', 'current'].includes(status(r))) },
        { value: 'upcoming', text: '以后生效', count: count(r => status(r) === 'upcoming') },
        { value: 'expired', text: '已过期', count: count(r => status(r) === 'expired') }
      ]
    },
    currentFilterText () {
      return this.periodFilters.find(f => f.value === this.periodFilter)?.text ?? ''
    },
    filteredList () {
      if (!this.supportsEffectiveDate || this.periodFilter === 'all') {
        return this.list
      }
      return this.list.filter(r => {
        const status = this.periodInfo(r).status
        if (this.periodFilter === 'current') {
          return status === 'always' || status === 'current'
        }
        return status === this.periodFilter
      })
    },
    schemas () {
      const rangeRule = () => {
        const from = this.formHolder?.effectiveFrom
        const to = this.formHolder?.effectiveTo
        return !from || !to || from <= to || '结束日期不能早于开始日期'
      }
      const dateFields = this.supportsEffectiveDate
        ? [
            {
              key: 'effectiveFrom',
              name: '生效开始日期',
              hint: '含当天，按预订的日期算；留空 = 不限',
              required: false,
              componentProps: { type: 'date', clearable: true, rules: [rangeRule] }
            },
            {
              key: 'effectiveTo',
              name: '生效结束日期',
              hint: '含当天；留空 = 不限',
              required: false,
              componentProps: { type: 'date', clearable: true, rules: [rangeRule] }
            }
          ]
        : []
      return [
        {
          key: 'price',
          hint: '套餐价格'
        },
        {
          key: 'comboBody',
          hint: '套餐详情',
          component: VTextarea
        },
        {
          key: 'active',
          name: 'isActive',
          component: VSelect,
          componentProps: {
            items: [
              { text: 'Yes', value: true },
              { text: 'No', value: false }
            ]
          },
          default: true
        },
        ...dateFields,
        {
          key: 'tableIds',
          name: '适用包厢',
          hint: '留空 = 所有包厢',
          required: false,
          component: VSelect,
          componentProps: {
            items: this.roomList.map(it => ({ text: it.name, value: it.id })),
            multiple: true,
            clearable: true
          }
        },
        ...this.startRangeFields()
      ]
    }
  },
  watch: {
    // 改了其中一个日期，另一个日期框的「结束不能早于开始」提示也要跟着刷新
    'formHolder.effectiveFrom' () {
      this.revalidateDateFields()
    },
    'formHolder.effectiveTo' () {
      this.revalidateDateFields()
    },
    'formHolder.startFromLocal' () {
      this.revalidateDateFields()
    },
    'formHolder.startToLocal' () {
      this.revalidateDateFields()
    }
  },
  mounted () {
    this.reloadData()
  },
  methods: {
    startRangeFields () {
      if (this.businessHourOffset === null) {
        return []
      }
      const rule = () => this.startRangeError(this.formHolder?.startFromLocal, this.formHolder?.startToLocal) || true
      return [
        {
          key: 'startFromLocal',
          name: '适用开始时段：从',
          hint: '按实际时间填；两个都留空 = 任何时段',
          required: false,
          componentProps: { type: 'time', clearable: true, rules: [rule] }
        },
        {
          key: 'startToLocal',
          name: '适用开始时段：到（不含）',
          hint: '不含这个时间；到营业日结束填 ' + this.toLocal('23:59'),
          required: false,
          componentProps: { type: 'time', clearable: true, rules: [rule] }
        }
      ]
    },
    // 实际时间 → 后台营业时间
    toBackend (hhmm) {
      return shiftHHmm(hhmm, -(this.businessHourOffset || 0))
    },
    // 后台营业时间 → 实际时间
    toLocal (hhmm) {
      return shiftHHmm(hhmm?.substring(0, 5), this.businessHourOffset || 0)
    },
    // 返回错误文案；没问题返回空串
    startRangeError (fromLocal, toLocal) {
      if (!fromLocal && !toLocal) return ''
      if (!fromLocal || !toLocal) return '开始时段的「从」和「到」要一起填，或者都留空'
      const from = this.toBackend(fromLocal)
      const to = this.toBackend(toLocal)
      if (!from || !to) return '时间格式不对'
      if (from >= to) {
        return '「到」要晚于「从」，且不能跨过营业日开始时间 ' + this.toLocal('00:00')
      }
      return ''
    },
    roomLabel (r) {
      if (!Array.isArray(r.tableIds) || r.tableIds.length === 0) return '全部'
      return r.tableIds.map(id => this.roomList.find(it => String(it.id) === String(id))?.name ?? ('#' + id)).join('、')
    },
    startRangeLabel (r) {
      if (!r.startFrom && !r.startTo) return '全部'
      if (this.businessHourOffset === null) return `${r.startFrom ?? ''}–${r.startTo ?? ''}（营业时间）`
      const from = this.toLocal(r.startFrom) ?? ''
      const to = this.toLocal(r.startTo) ?? ''
      // 实际时间跨过午夜（比如 20:00–05:59）时标一下「次日」
      return `${from}–${to < from ? '次日 ' : ''}${to}`
    },
    revalidateDateFields () {
      this.$nextTick(() => {
        const inputs = this.$refs.comboForm?.$refs.form?.inputs ?? []
        // 只刷新两个日期框（都是选填，空值不会报错），不碰价格等必填项
        inputs.filter(i => ['date', 'time'].includes(i.type)).forEach(i => i.validate(true))
      })
    },
    periodLabel (r) {
      const from = r.effectiveFrom
      const to = r.effectiveTo
      if (from && to) return `${from} 至 ${to}`
      if (from) return `${from} 起`
      if (to) return `到 ${to}`
      return '一直有效'
    },
    periodInfo (r) {
      let status = 'current'
      if (!r.effectiveFrom && !r.effectiveTo) {
        status = 'always'
      } else if (r.effectiveFrom && r.effectiveFrom > this.today) {
        status = 'upcoming'
      } else if (r.effectiveTo && r.effectiveTo < this.today) {
        status = 'expired'
      }
      return { status, ...PERIOD_STATUS[status] }
    },
    edit (obj) {
      // 表单里开始时段按实际时间显示；保存时再换回后台营业时间
      this.editingObject = {
        ...obj,
        tableIds: Array.isArray(obj.tableIds) ? obj.tableIds.map(Number) : [],
        startFromLocal: obj.startFrom ? this.toLocal(obj.startFrom) : null,
        startToLocal: obj.startTo ? this.toLocal(obj.startTo) : null
      }
      this.showDialog = true
    },
    async remove (item) {
      await sureToDelete(async () => {
        await deletePriceCombo(item.id)
        await this.reloadData()
      })
    },
    async save (item) {
      // addOrUpdate 是整条覆盖：表单里带着原对象的所有字段，这里只把换算后的值写回去
      const { startFromLocal, startToLocal, ...payload } = item
      payload.tableIds = Array.isArray(payload.tableIds) ? payload.tableIds : []
      if (this.businessHourOffset !== null) {
        const error = this.startRangeError(startFromLocal, startToLocal)
        if (error) {
          this.$store.commit('showTipsDialog', { tipsMessage: error })
          return
        }
        payload.startFrom = startFromLocal ? this.toBackend(startFromLocal) : null
        payload.startTo = startToLocal ? this.toBackend(startToLocal) : null
      }
      if (this.supportsEffectiveDate) {
        payload.effectiveFrom = emptyToNull(payload.effectiveFrom)
        payload.effectiveTo = emptyToNull(payload.effectiveTo)
        if (payload.effectiveFrom && payload.effectiveTo && payload.effectiveFrom > payload.effectiveTo) {
          this.$store.commit('showTipsDialog', { tipsMessage: '结束日期不能早于开始日期' })
          return
        }
      }
      this.dialogLoading = true
      const res = await savePriceCombo(payload)
      this.dialogLoading = false
      if (res && res.code !== undefined && res.code !== 200) {
        this.$store.commit('showTipsDialog', { tipsMessage: res.message || '保存失败' })
        return
      }
      this.showDialog = false
      this.editingObject = null
      await this.reloadData()
    },

    async reloadData () {
      this.loading = true
      const [comboResult, roomResult, settingResult] = await Promise.allSettled([
        getAllPriceCombo(),
        Table.getList(),
        checkReservationStatus()
      ])
      if (roomResult.status === 'fulfilled') {
        this.roomList = (roomResult.value ?? []).filter(it => it.tableType === 'Room')
      }
      const offset = settingResult.status === 'fulfilled' ? settingResult.value?.setting?.businessHourOffset : null
      this.businessHourOffset = Number.isFinite(Number(offset)) && offset !== null && offset !== undefined ? Number(offset) : null
      if (comboResult.status === 'fulfilled') {
        const { list, supportsEffectiveDate } = comboResult.value
        this.list = list
        this.supportsEffectiveDate = supportsEffectiveDate
      }
      this.loading = false
    }
  }
}
</script>

<style scoped>

</style>
